import { Download, Filter, RotateCcw, Search, X } from '../../../icons';
import {
  CLEAR_FILTERS_LABEL, PENDING_FILTER_REASON, activeFilterCount, type ApprovalFilters,
} from './approval-filters';
import './approval-toolbar.css';

/**
 * 결재함 툴바 — **문서 목록 «바로 위»** (2026-09-17 · 사용자 요구).
 *
 * 사용자 요구 넷:
 *   ① 검색 입력을 문서 목록 위로  ② 관련 단추도 같은 줄로
 *   ③ 조건필터 아이콘 → 누르면 조건 상세  ④ 조건필터 «옆»에 필터 지우기
 *
 * ★ 정본 근거 — P3 「목록」의 **「필터바 → 표 → 페이징 3층」**. 새로 만드는 규율이 아니라
 *   지금 화면이 **안 지키던 것**이다(검색이 페이지 머리에 있고 그 사이를 KPI 넷이 막고 있었다).
 * ★ 높이는 A4 Toolbar 규율 — 바 `--h-lg`(40) · 안의 컨트롤 `--h-sm`(28) · **가로 스크롤 금지**(넘치면 접힌다).
 *
 * ★ 낱말은 **「필터 지우기」**로 정해졌다(2026-09-17 사용자 결정 · `CLEAR_FILTERS_LABEL`).
 *   사용자가 처음 쓴 「조건초기화」 대신 **정본 P3 의 문구**를 택하셨다 — 「초기화」를 섞지 않는다.
 *
 * ★ 조건 상세는 **「펼침」**으로 정해졌다(2026-09-17 사용자 결정 — 서랍 · 모달은 버린다).
 *   이 부품은 «열렸나»만 알고 «어떻게» 열리는지는 모른다 — 패널은 바깥이 표 위에 그린다.
 */
export interface ApprovalFilterBarProps {
  filters: ApprovalFilters;
  onChange: (next: ApprovalFilters) => void;
  /** 조건 상세가 열려 있나 — 여는 «방식»은 이 부품이 정하지 않는다. */
  open: boolean;
  onToggleOpen: () => void;
  /** 필터 지우기 — **조건만** 지우고 검색어는 남긴다(`clearFilters`). */
  onClear: () => void;
  /** 낱말은 `CLEAR_FILTERS_LABEL` 이 정본 — 바꿀 일이 있을 때만 바깥에서 덮는다. */
  clearLabel?: string;
  /** 내려받기 — 서버 라우트는 있으나 화면 연결은 이번 범위 밖이라 꺼 둔다. */
  downloadDisabled?: boolean;
  downloadTitle?: string;
}

export function ApprovalFilterBar({
  filters, onChange, open, onToggleOpen, onClear,
  clearLabel, downloadDisabled = true, downloadTitle,
}: ApprovalFilterBarProps): JSX.Element {
  const count = activeFilterCount(filters);
  const clearTitle = clearLabel ?? CLEAR_FILTERS_LABEL;

  return (
    <div className="apf-bar" role="search" aria-label="문서 찾기">
      <label className="apf-search">
        <span className="apb-sr">문서번호 · 제목 · 기안자 검색</span>
        <Search aria-hidden />
        {/* ★ 회귀 금지 — 치는 동안 300ms 모았다가 문서함 넷 + involved 에 `q` 를 싣는 동작은
              바깥(ApprovalBoxPage)의 effect 가 그대로 한다. 여기서는 값만 올려 준다. */}
        <input
          type="search"
          value={filters.q}
          maxLength={100}
          placeholder="문서번호, 제목, 기안자로 찾기"
          onChange={(e) => onChange({ ...filters, q: e.target.value })}
        />
      </label>

      {/* ③ 조건필터 — 걸린 수를 숫자로 (접혀 있어도 «몇 개»가 보인다) */}
      <button
        type="button"
        className={`apf-btn${open || count > 0 ? ' apf-btn--on' : ''}`}
        aria-expanded={open}
        aria-controls="apf-panel"
        onClick={onToggleOpen}
      >
        <Filter aria-hidden />
        조건필터
        {count > 0 && <span className="apf-count" aria-label={`걸린 조건 ${count}개`}>{count}</span>}
      </button>

      {/* ④ 조건필터 «옆» — 지우기. 걸린 것이 없으면 누를 것이 없으므로 끈다. */}
      <button
        type="button"
        className="apf-btn apf-btn--icon"
        onClick={onClear}
        disabled={count === 0}
        title={clearTitle}
        aria-label={clearTitle}
      >
        <RotateCcw aria-hidden />
      </button>

      <span className="apf-div" aria-hidden />

      <button
        type="button"
        className="apf-btn"
        disabled={downloadDisabled}
        title={downloadTitle}
      >
        <Download aria-hidden />
        내려받기
      </button>
    </div>
  );
}

/**
 * 걸린 조건을 «무엇이» 걸렸는지로 말하는 칩 줄.
 * ★ 숫자 배지는 「몇 개」를, 칩은 「무엇이」를 말한다 — 둘 다 있어야 사용자가
 *   **왜 목록이 적은지** 안다(정본 P3 「필터 칩에 누르면 몇 개 보이나를 적는다」와 같은 뜻).
 */
export interface ApprovalFilterChipsProps {
  chips: ReadonlyArray<{ key: string; label: string; onRemove: () => void }>;
}

export function ApprovalFilterChips({ chips }: ApprovalFilterChipsProps): JSX.Element | null {
  if (chips.length === 0) return null;
  return (
    <div className="apf-chips" aria-label="걸린 조건">
      {chips.map((c) => (
        <span className="apf-chip" key={c.key}>
          {c.label}
          <button type="button" onClick={c.onRemove} aria-label={`${c.label} 지우기`}>
            <X aria-hidden />
          </button>
        </span>
      ))}
    </div>
  );
}

/** 「서버 준비 중」 칸에 붙는 꼬리표 — 흐린 칸이 왜 흐린지 말한다. */
export function PendingTag(): JSX.Element {
  return <span className="apf-soon">{PENDING_FILTER_REASON}</span>;
}
